import React from "react";
import { motion } from "framer-motion";
import { Video, Users2, Phone, MapPin, Calendar, Globe } from "lucide-react";
import { BRAND } from "../config/brand";
import { useLang } from "../i18n/LangContext";

const ICONS = [Video, Users2, Phone, MapPin, Calendar, Globe];

export const Online = () => {
  const { t } = useLang();
  const items = t("online.items");

  return (
    <section
      data-testid="section-online"
      className="relative py-24 lg:py-32 bg-[#050505] overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] lg:aspect-[3/4] overflow-hidden">
              <img
                src={BRAND.onlineImage}
                alt="Online Coaching"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/40 to-transparent" />
              <div className="absolute inset-0 border border-white/10" />

              <div className="absolute top-6 left-6 glass px-4 py-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary-red animate-pulse" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                    LIVE · WORLDWIDE
                  </span>
                </div>
              </div>

              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-1">
                    Global
                  </div>
                  <div className="font-display text-2xl lg:text-3xl font-bold text-white">
                    24/7 Availability
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                    Timezone
                  </div>
                  <div className="font-display text-xl font-semibold text-white">
                    Flex
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-primary-red" />
              <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-400">
                {t("online.eyebrow")}
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              {t("online.title")}
            </h2>
            <p className="mt-6 font-body text-base lg:text-lg text-zinc-400 leading-relaxed max-w-xl">
              {t("online.p")}
            </p>

            <div className="mt-10 grid grid-cols-2 gap-3">
              {items.map((it, i) => {
                const Icon = ICONS[i % ICONS.length];
                return (
                  <div
                    key={i}
                    data-testid={`online-item-${i}`}
                    className="flex items-center gap-3 border border-white/10 bg-[#0a0a0a] hover:border-primary-red/60 hover:bg-[#0f0f0f] p-4 transition-colors"
                  >
                    <Icon size={16} className="text-primary-red shrink-0" />
                    <span className="font-body text-sm text-zinc-200">{it}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
